import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { RiPencilLine } from "react-icons/ri";
import FailureView from "../FailureView";
import LoadingView from "../LoadingView";
import SlidingModal from "../SlidingModal/slidingModal";
import EditEmployeeModal from "../EditEmployeeModal";
import "./index.css";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const ReleasedEmployeeTable = ({
  employeesList,
  apiStatus,
  refreshEmployeesList,
  leads,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(FEATURE_CODES.EMPLOYEE_EDIT);

  const handleRowClicked = (params) => {
    if (params.event.target.closest(".no-row-nav")) return;
    navigate(`/employee/${params.data.id}`, {
      state: { employee: params.data, leads, returnPath: "/allemployees" },
    });
  };

  const [singleEmployeeData, setSingleEmployeeData] = useState({});
  const [editEmployeeDetailsModal, setEditEmployeeDetailsModal] =
    useState(false);
  const notifyError = () => toast.error("Employee failed to update");
  const notifyUpdated = () => toast.success("Employee Updated Successfully");

  const closeEditEmployeeModel = () => {
    setEditEmployeeDetailsModal(false);
  };

  const noDataDisplay = () => (
    <div className="d-flex flex-column justify-content-center align-items-center">
      <img
        className="noData-img"
        alt="NoData"
        src="https://img.freepik.com/free-vector/no-data-concept-illustration_114360-536.jpg?w=740&t=st=1685691455~exp=1685692055~hmac=e7fe93b465ad04cefb40b139444400f1cb0f9068a880f91999b843299068fdca"
      />

      <h3>No Records Found</h3>
    </div>
  );

  const defaultColDef = useMemo(() => {
    return {
      cellStyle: { fontSize: "14px" },
    };
  }, []);

  const paginationPageSize = 20;

  const columnDefs = [
    ...(canEdit
      ? [
          {
            headerName: "",
            width: 50,
            pinned: "left",
            cellStyle: { paddingRight: 0 },
            cellClass: "no-row-nav",
            cellRenderer: (params) => (
              <RiPencilLine
                id={params.data.id}
                onClick={() => {
                  setSingleEmployeeData(params.data);
                  setEditEmployeeDetailsModal(true);
                }}
                style={{ fontSize: "20px", cursor: "pointer" }}
              />
            ),
          },
        ]
      : []),
    {
      headerName: "EMP ID",
      field: "id",
      width: 150,
      sortable: true,
      pinned: "left",
      floatingFilter: true,
      filter: true,
      suppressMenu: true
    },
    {
      headerName: "NAME",
      cellRenderer: (params) => {
        return <p>{params.data.fullName}</p>;
      },
      field: "fullName",
      pinned: "left",
      tooltipField: "fullName",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },

    {
      headerName: "EMAIL",
      field: "email",
      width: 350,
      filter: true,
      floatingFilter: true,
      tooltipField: "email",
      suppressMenu: true
    },
    {
      headerName: "ROLE",
      field: "role",
      width: 130,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
     {
      headerName: "LEAD",
      field: "leadName",
      width: 130,
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      cellRenderer: (params) => params.value || "-",
          cellStyle: (params) => ({
            textAlign: params.value ? "left" : "center"
          }),
    },
    {
      headerName: "DESIGNATION",
      field: "designation",
      width: 200,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "DEPARTMENT",
      field: "department",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "QUALIFICATIONS",
      field: "qualifications",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "JOINING DATE",
      field: "joiningDate",
      width: 180,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "TOTAL EXPERIENCE",
      width: 300,
      field: "totalExperience",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "ORGANIZATION EXPERIENCE",
      width: 300,
      field: "organizationExperience",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "RELEASED DATE",
      field: "releaseDate",
      width: 180,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "RELEASE TYPE",
      field: "releasedType",
      suppressMenu: true,
      filter: true,
      floatingFilter: true,
      cellRenderer: (params) => params.value || "-",
          cellStyle: (params) => ({
            textAlign: params.value ? "left" : "center"
          }),
    },
    {
      headerName: "EMP STATUS",
      field: "employementStatus",
      width: 180,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "DOB",
      field: "dateOfBirth",
      width: 120,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "GENDER",
      field: "gender",
      width: 150,
      cellStyle: { display: "flex", justifyContent: "center" },
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "BLOOD GROUP",
      field: "bloodGroup",
      width: 180,
      cellStyle: { display: "flex", justifyContent: "center" },
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "MOBILE NO",
      field: "mobileNumber",
      width: 150,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "EMG CONTACT NAME",
      field: "emergencyContactName",
      width: 250,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },

    {
      headerName: "EMG CONTACT NUMBER",
      field: "emergencyContactNumber",
      width: 250,
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "LOCATION",
      field: "address",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "PF CLOSURE STATUS",
      field: "pfClosureStatus",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "UAN CLOSURE STATUS",
      field: "uanClosureStatus",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
    {
      headerName: "HEALTH CARD CLOSURE STATUS",
      field: "healthCardClosureStatus",
      filter: true,
      floatingFilter: true,
      suppressMenu: true
    },
  ];

  const renderTable = () => (
    <div
      className="ag-theme-alpine released-employees-grid-wrapper"
      style={{
        height: "78vh",
        fontSize: "14px",
        marginLeft: "20px",
        marginRight: "20px",
        fontFamily: "Arial, Helvetica, sans-serif",
        backgroundColor: "#FFFFFF",
      }}
    >
      <AgGridReact
        title="All Employees"
        columnDefs={columnDefs}
        rowData={employeesList}
        pagination={true}
        paginationPageSize={paginationPageSize}
        defaultColDef={defaultColDef}
        rowSelection="multiple"
        // onGridReady={onGridReady}
        // onSelectionChanged={onSelectionChanged}
        onRowClicked={handleRowClicked}
        suppressRowClickSelection="true"
        suppressCellFocus={true}
        enableCellTextSelection={true}
        rowStyle={{ cursor: "pointer" }}
      ></AgGridReact>
    </div>
  );

  const renderEmployeeTable = () => (
    <div className="table-container">
      {employeesList.length > 0 ? renderTable() : noDataDisplay()}
    </div>
  );

  const onClickRetry = () => refreshEmployeesList();

  const renderAllEmployeesPage = () => {
    switch (apiStatus) {
      case apiStatusConstants.success:
        return renderEmployeeTable();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={onClickRetry} />;
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderAllEmployeesPage()}

      <SlidingModal
        modalName="Edit Employee"
        closeBtn={closeEditEmployeeModel}
        isModalOpen={editEmployeeDetailsModal}
      >
        <EditEmployeeModal
          fetchEmployees={refreshEmployeesList}
          notifyUpdated={notifyUpdated}
          notifyError={notifyError}
          singleEmployeeData={singleEmployeeData}
          closeEditEmployeeModel={closeEditEmployeeModel}
          leads={leads}
        />
      </SlidingModal>
    </>
  );
};

export default ReleasedEmployeeTable;
